import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { EnrollmentStatusBadge } from '@/components/courses/EnrollmentStatusBadge';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { enrollmentSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { usePermissions } from '@/hooks/usePermissions';
import { enrollmentShowBack, searchSuffix } from '@/lib/enrollment-navigation';
import { studentProfilePath } from '@/lib/user-detail-navigation';

interface EnrollmentDetail {
  id: string;
  user?: { id: string; name: string; email: string };
  course?: { id: string; title: string; name?: string };
  assignableItem?: { title?: string };
  enrollmentType?: { name?: string };
  enrolledAt?: string;
  expiresAt?: string | null;
  status?: string;
  progress?: number;
  progressPercent?: number;
  assignedBy?: { name?: string };
  group?: { name?: string };
  origin?: string;
}

const actionButtonClass =
  'inline-flex shrink-0 items-center whitespace-nowrap rounded-lg border border-dash-outline-variant bg-white px-4 py-2 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-100 disabled:opacity-60';

export function EnrollmentShowPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.INSCRIPCIONES_ADMINISTRAR);
  const canViewStudentProfile = hasPermission(PERMISSIONS.USUARIOS_VER) || hasPermission(PERMISSIONS.ESTUDIANTES_PERFIL_VER);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const back = enrollmentShowBack(searchParams);
  const suffix = searchSuffix(searchParams);

  const { data, isLoading, error } = useQuery({
    queryKey: ['enrollments', id],
    queryFn: () => api<EnrollmentDetail>(`/enrollments/${id}`),
    enabled: Boolean(id),
  });

  const suspendMutation = useMutation({
    mutationFn: () => api(`/enrollments/${id}/suspend`, { method: 'PATCH' }),
    onSuccess: () => {
      setSuspendOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['enrollments', id] });
      void queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/enrollments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      toast(enrollmentSuccessToast('delete'));
      void queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      navigate(back.to);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la matrícula'));
    },
  });

  const courseTitle = data?.course?.title ?? data?.course?.name ?? data?.assignableItem?.title ?? 'Curso';
  const progress = data?.progressPercent ?? data?.progress ?? 0;
  const isSuspended = data?.status === 'suspended';

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <header className="mb-8 flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="flex min-w-0 items-start gap-4">
          <HubBackLink to={back.to} label={back.label} />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Detalle de Matrícula</h1>
            <p className="mt-1 text-sm text-dash-on-surface-variant md:text-base">
              {data?.user?.name ?? 'Estudiante'} · {courseTitle}
            </p>
          </div>
        </div>
        {canManage && data && (
          <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
            <Link to={`/inscripciones/${id}/edit${suffix}`} className={actionButtonClass}>
              Editar
            </Link>
            {!isSuspended && (
              <button type="button" className={actionButtonClass} onClick={() => setSuspendOpen(true)}>
                Suspender
              </button>
            )}
            <button
              type="button"
              className="inline-flex shrink-0 items-center whitespace-nowrap rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
              onClick={() => setDeleteOpen(true)}
            >
              Eliminar matrícula
            </button>
          </div>
        )}
      </header>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
          <section className="lms-credentials-glass-panel overflow-hidden rounded-xl border border-dash-outline-variant shadow-sm lg:col-span-1">
            <h2 className="border-b border-dash-outline-variant px-6 py-4 text-lg font-semibold text-dash-primary">
              Resumen
            </h2>
            <dl className="px-6 py-2 text-sm">
              <div className="flex items-center justify-between gap-4 border-b border-secondary-100 py-3">
                <dt className="text-dash-on-surface-variant">Estado</dt>
                <dd>
                  <EnrollmentStatusBadge status={data?.status} />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-b border-secondary-100 py-3">
                <dt className="text-dash-on-surface-variant">Tipo</dt>
                <dd className="text-right font-medium text-dash-on-surface">{data?.enrollmentType?.name ?? '—'}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-b border-secondary-100 py-3">
                <dt className="text-dash-on-surface-variant">Inscrito el</dt>
                <dd className="text-right font-medium text-dash-on-surface">
                  {data?.enrolledAt ? new Date(data.enrolledAt).toLocaleDateString('es') : '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-b border-secondary-100 py-3">
                <dt className="text-dash-on-surface-variant">Vigencia</dt>
                <dd className="text-right font-medium text-dash-on-surface">
                  {data?.expiresAt ? new Date(data.expiresAt).toLocaleDateString('es') : 'Sin vencimiento'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-b border-secondary-100 py-3">
                <dt className="text-dash-on-surface-variant">Asignado por</dt>
                <dd className="text-right font-medium text-dash-on-surface">{data?.assignedBy?.name ?? '—'}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-3">
                <dt className="text-dash-on-surface-variant">Origen</dt>
                <dd className="text-right font-medium text-dash-on-surface">{data?.origin ?? '—'}</dd>
              </div>
            </dl>
            {progress > 0 && (
              <div className="mx-6 mb-6 rounded-lg bg-dash-primary p-4 text-white">
                <p className="text-sm font-medium">{progress}% completado</p>
              </div>
            )}
          </section>

          <section className="lms-credentials-glass-panel overflow-hidden rounded-xl border border-dash-outline-variant shadow-sm lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-dash-outline-variant px-6 py-4">
              <h2 className="text-lg font-semibold text-dash-primary">Historial</h2>
              {data?.user && canViewStudentProfile ? (
                <Link
                  to={studentProfilePath(data.user.id, `${location.pathname}${location.search}`)}
                  className="text-sm font-medium text-dash-secondary hover:underline"
                >
                  Ver perfil del estudiante
                </Link>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-dash-outline-variant bg-secondary-50/80">
                    <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                      Fecha y hora
                    </th>
                    <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                      Acción
                    </th>
                    <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                      Cambio de estado
                    </th>
                    <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                      Realizado por
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-dash-on-surface-variant">
                      Sin eventos registrados
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      <ConfirmModal
        open={suspendOpen}
        title="Suspender matrícula"
        description={`¿Suspender la matrícula de «${data?.user?.name ?? 'este estudiante'}» en «${courseTitle}»?`}
        confirmLabel="Suspender"
        loading={suspendMutation.isPending}
        onConfirm={() => suspendMutation.mutate()}
        onClose={() => {
          if (!suspendMutation.isPending) setSuspendOpen(false);
        }}
      />

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar matrícula"
        description={`¿Está seguro de eliminar la matrícula de «${data?.user?.name ?? 'este estudiante'}» en «${courseTitle}»?`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
}
